import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import { isPretendAiMessage } from '../utils/ChatMessage';
import { type ChatMessage } from '../types/ChatMessage.types';
import { getAiMessageStreaming } from '../redux/chat';

const TOP_BUFFER_PIXELS = 100;

// eslint-disable-next-line max-lines-per-function
export function useScrollToBottomOfChat(messagesWithRefs: { message: ChatMessage; ref: RefObject<HTMLDivElement> }[]) {
    const chatWindowRef = useRef<HTMLDivElement | null>(null);
    const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);

    const previousChatHistoryLength = usePrevious(messagesWithRefs.length) ?? 0;

    const aiMessageStreaming = useSelector(getAiMessageStreaming);

    const allMessagesArePretendAiMessages = useMemo(
        () => messagesWithRefs.every(message => isPretendAiMessage(message.message)),
        [messagesWithRefs],
    );

    const streamingMessageIsAtTop = useMemo(() => {
        if (!aiMessageStreaming) return false;

        const { ref: streamingMessageRef } = messagesWithRefs.at(-1)!;
        if (!streamingMessageRef.current) return false;

        const { top } = streamingMessageRef.current.getBoundingClientRect();
        const { top: chatWindowTop } = chatWindowRef.current!.getBoundingClientRect();
        return top <= chatWindowTop + TOP_BUFFER_PIXELS;
    }, [aiMessageStreaming, messagesWithRefs]);

    const isAtBottom = useMemo(
        () => chatWindowRef.current?.scrollTop === chatWindowRef.current?.scrollHeight,
        [chatWindowRef],
    );

    //---------------------------
    // Scroll to bottom on new message
    //---------------------------

    const scrollToBottomOfChat = useCallback(
        ({ smooth = true }: { smooth?: boolean } = {}) => {
            // Only use scrollTo if browser supports it and when not in the middle of a
            // streaming bot response. The smooth scroll can cause jitters while streaming.
            if (chatWindowRef.current?.scrollTo && smooth) {
                chatWindowRef.current.scrollTo({
                    top: chatWindowRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            } else if (chatWindowRef.current) {
                chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
            }
        },
        [chatWindowRef],
    );

    useEffect(() => {
        if (streamingMessageIsAtTop) return;
        if (aiMessageStreaming) {
            // Scrolling smoothly while streaming can cause jitters.
            scrollToBottomOfChat({ smooth: false });
            return;
        }
        if (messagesWithRefs.length > previousChatHistoryLength) {
            scrollToBottomOfChat();
        }
    }, [
        streamingMessageIsAtTop,
        scrollToBottomOfChat,
        aiMessageStreaming,
        previousChatHistoryLength,
        isAtBottom,
        messagesWithRefs,
    ]);

    useEffect(() => {
        let resizeObserver: ResizeObserver;
        let windowResizeListener: () => void;

        if (chatMessagesContainerRef.current) {
            resizeObserver = new ResizeObserver(() => {
                // Scroll to bottom as new greeting messages are added
                if (!allMessagesArePretendAiMessages) return;
                scrollToBottomOfChat();
            });

            resizeObserver.observe(chatMessagesContainerRef.current);

            // this one is necessary for when keyboard pops up in Cordova
            windowResizeListener = () => {
                scrollToBottomOfChat({ smooth: !aiMessageStreaming });
            };

            window.addEventListener('resize', windowResizeListener);
        }

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            if (windowResizeListener) window.removeEventListener('resize', windowResizeListener);
        };
    }, [
        chatMessagesContainerRef,
        scrollToBottomOfChat,
        aiMessageStreaming,
        streamingMessageIsAtTop,
        allMessagesArePretendAiMessages,
    ]);

    return {
        chatWindowRef,
        chatMessagesContainerRef,
    };
}
